/**
 * useShellBootstrap — React hook for the production shell bootstrap sequence.
 *
 * Returns { isLoading, context, reload }.
 *
 * Lifecycle:
 *   - On mount: sets isLoading=true, calls loadShellBootstrapContext, stores
 *     the result and sets isLoading=false.
 *   - reload(): repeats the same sequence.
 *   - On unmount: cancels any in-flight load so setState is never called after
 *     the component is gone.
 *
 * Does not use Redux or wsClient.
 *
 * @param {object} bridge - BridgeClient instance (sendMessageWithResponse)
 * @returns {{ isLoading: boolean, context: object|null, reload: function }}
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { loadShellBootstrapContext } from './loadShellBootstrapContext';

export function useShellBootstrap(bridge) {
  const [isLoading, setIsLoading] = useState(true);
  const [context, setContext] = useState(null);
  // Incremented by reload() to re-trigger the useEffect.
  const [loadCount, setLoadCount] = useState(0);
  // Tracks whether the component is still mounted.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);

    loadShellBootstrapContext(bridge)
      .then((ctx) => {
        if (!cancelled && mountedRef.current) {
          setContext(ctx);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        // loadShellBootstrapContext is documented as «never throws»; this catch is
        // defense-in-depth against unexpected regressions (e.g. a lane normalizer
        // missing a try/catch).  Without it a rejection becomes
        // «Uncaught (in promise)» and isLoading stays true indefinitely.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('[useShellBootstrap] unexpected rejection:', err);
        }
        if (!cancelled && mountedRef.current) {
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  // bridge is a stable useRef; loadCount is incremented by reload() only.
  }, [loadCount]);

  const reload = useCallback(() => {
    setLoadCount((c) => c + 1);
  }, []);

  return { isLoading, context, reload };
}
